import React from 'react'
import PropTypes from 'prop-types'
import { TextField } from '@mui/material'
import { styled } from '@mui/material/styles'

// Helper function to get the border color based on state
const getBorderColor = (success, warning, theme) => {
  if (success) return theme.palette.success.main
  if (warning) return theme.palette.warning.main
  return theme.palette.primary.main
}

// Helper function to get margin size
const getMarginSize = (noMargin, compact, theme) => {
  if (noMargin) return 0
  if (compact) return theme.spacing(2)
  return theme.spacing(4)
}

// List of props that should not be forwarded to the DOM
const shouldForwardProp = (prop) => !['success', 'warning', 'compact', 'noMargin'].includes(prop)

// Styled TextField component with all the custom styles
const StyledTextField = styled(TextField, {
  shouldForwardProp,
})(({ theme, success, warning, compact, noMargin }) => {
  const focusedColor = getBorderColor(success, warning, theme)
  const marginSize = getMarginSize(noMargin, compact, theme)

  return {
    marginBottom: marginSize,

    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: focusedColor,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.error.main,
      },
    },

    '& .MuiInputLabel-root': {
      color: '#666666',
      '&.Mui-focused': {
        color: focusedColor,
      },
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },
  }
})

/**
 * Shared MUI TextField wrapper component
 * Replaces muicss Input pattern with consistent styling
 * Uses forwardRef to support ref forwarding for react-autosuggest compatibility
 */
const MuiTextFieldWrapper = React.forwardRef(
  (
    {
      label,
      name,
      value,
      defaultValue,
      onChange,
      onBlur,
      className,
      disabled = false,
      required = false,
      error = false,
      helperText,
      type = 'text',
      multiline = false,
      rows,
      maxRows,
      minRows,
      placeholder,
      fullWidth = true,
      variant = 'outlined',
      success = false,
      warning = false,
      compact = false,
      noMargin = false,
      ...otherProps
    },
    ref,
  ) => {
    // Support both controlled (value) and uncontrolled (defaultValue) patterns
    const inputProps = {}
    if (value !== undefined) {
      inputProps.value = value || ''
    } else if (defaultValue !== undefined) {
      inputProps.defaultValue = defaultValue
    }

    return (
      <StyledTextField
        ref={ref}
        className={className}
        label={label}
        name={name}
        {...inputProps}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        error={error}
        helperText={helperText}
        type={type}
        multiline={multiline}
        rows={rows}
        maxRows={maxRows}
        minRows={minRows}
        placeholder={placeholder}
        fullWidth={fullWidth}
        variant={variant}
        success={success}
        warning={warning}
        compact={compact}
        noMargin={noMargin}
        {...otherProps}
      />
    )
  },
)

MuiTextFieldWrapper.displayName = 'MuiTextFieldWrapper'

MuiTextFieldWrapper.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  type: PropTypes.string,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  maxRows: PropTypes.number,
  minRows: PropTypes.number,
  placeholder: PropTypes.string,
  fullWidth: PropTypes.bool,
  variant: PropTypes.string,
  success: PropTypes.bool,
  warning: PropTypes.bool,
  compact: PropTypes.bool,
  noMargin: PropTypes.bool,
}

export default MuiTextFieldWrapper
